import React from 'react';
import { useTranslation } from 'react-i18next';
import { Phone, Mail } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Footer = () => {
  const { t } = useTranslation();
  
  const handleWhatsAppClick = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/whatsapp-click`);
    } catch (error) {
      console.error('Error tracking WhatsApp click:', error);
    }
  };
  
  return (
    <footer className="bg-gray-900 text-white py-12">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-4 gap-8">
          {/* Logo and Description */}
          <div className="md:col-span-2">
            <h3 className="text-2xl font-bold text-[#0052CC] mb-4">Residencial Los Jardines</h3>
            <p className="text-gray-400 mb-4">
              {t('footer.description')}
            </p>
            
            {/* Logos */}
            <div className="flex items-center gap-4 bg-white px-4 py-3 rounded-lg mb-4 w-fit">
              <img 
                src="https://customer-assets.emergentagent.com/job_los-jardines-blanes/artifacts/m1z7a1k3_%D0%A1%D0%BD%D0%B8%D0%BC%D0%BE%D0%BA%20%D1%8D%D0%BA%D1%80%D0%B0%D0%BD%D0%B0%202025-10-29%20%D0%B2%2010.03.56.png" 
                alt="Застройщик" 
                className="h-8 object-contain"
              />
              <div className="w-px h-6 bg-gray-300"></div>
              <img 
                src="https://customer-assets.emergentagent.com/job_los-jardines-blanes/artifacts/ejnnimsx_%D0%A1%D0%BD%D0%B8%D0%BC%D0%BE%D0%BA%20%D1%8D%D0%BA%D1%80%D0%B0%D0%BD%D0%B0%202025-10-29%20%D0%B2%2015.47.11.png" 
                alt="Estate Barcelona" 
                className="h-8 object-contain"
              />
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4">{t('footer.quickLinks')}</h4>
            <ul className="space-y-2">
              <li>
                <a href="#about" className="text-gray-400 hover:text-[#0052CC] transition-colors">
                  {t('header.about')}
                </a>
              </li>
              <li>
                <a href="#layouts" className="text-gray-400 hover:text-[#0052CC] transition-colors">
                  {t('header.layouts')}
                </a>
              </li>
              <li>
                <a href="#amenities" className="text-gray-400 hover:text-[#0052CC] transition-colors">
                  {t('header.amenities')}
                </a>
              </li>
              <li>
                <a href="#location" className="text-gray-400 hover:text-[#0052CC] transition-colors">
                  {t('header.location')}
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-4">{t('footer.contacts')}</h4>
            <ul className="space-y-3 text-gray-400">
              <li className="flex items-start gap-2">
                <Phone className="w-4 h-4 mt-1 flex-shrink-0" />
                <a href="tel:+34932205680" className="hover:text-[#0052CC] transition-colors">
                  +34(932)205-680
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Mail className="w-4 h-4 mt-1 flex-shrink-0" />
                <a href="mailto:office@estate-barcelona.com" className="hover:text-[#0052CC] transition-colors break-all">
                  office@estate-barcelona.com
                </a>
              </li>
              <li className="mt-4">
                <a 
                  href="https://wa.me/34932205680?text=Хочу%20больше%20информации%20по%20новостройке%20в%20Бланес"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleWhatsAppClick}
                  className="inline-flex items-center gap-2 bg-[#25D366] text-white px-4 py-2 rounded-lg hover:bg-[#20BA5A] transition-colors"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  WhatsApp
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400 text-sm">
          <p>&copy; 2025 Residencial Los Jardines. {t('footer.rights')}.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;